import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { validateEmail } from 'src/client/utils/validators';
import { useSelector } from 'src/client/redux/modules/helpers/useSelector';
import cx from 'classnames';

export enum Status {
  NOT_SENT = 'NOT_SENT',
  SENT = 'SENT',
  OPTED_IN = 'OPTED_IN',
}

type Props = {
  submitBtnClassName?: string;
  status: Status;
  submitting: boolean;
  disabled: boolean;
  onSubmit: (values: { email?: string; type?: string }) => void;
  submitFailed: boolean;
};

export const NewsletterForm = (props: Props) => {
  const { disabled, onSubmit, status, submitBtnClassName, submitFailed, submitting } = props;

  const email = useSelector((state) => state.auth.user?.email);
  const [emailValue, setEmailValue] = useState(email || '');
  const [touched, setTouched] = useState(false);

  const emailError = validateEmail(emailValue);
  const isEmailInvalid = !!emailError && emailValue !== '';
  const canSubmit = !disabled && !submitting && !isEmailInvalid;

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    onSubmit({
      email: emailValue,
      type: 'TOP_10',
    });
  };

  const renderGuestForm = () => (
    <fieldset disabled={submitting || disabled} className="inline-block">
      <form onSubmit={handleSubmit}>
        <Input
          type="email"
          name="email"
          className={isEmailInvalid && touched ? 'has-error' : ''}
          disabled={disabled}
          autoComplete="email"
          placeholder="Email Address"
          value={emailValue}
          onChange={(event) => setEmailValue(event.target.value)}
          onBlur={() => setTouched(true)}
        />
        <SubmitButtonBase type="submit" className={cx('btn btn-primary', submitBtnClassName)} disabled={!canSubmit}>
          Subscribe
        </SubmitButtonBase>
      </form>
    </fieldset>
  );

  const renderMemberForm = () => (
    <fieldset disabled={submitting || disabled} className="block">
      <form onSubmit={handleSubmit}>
        <SubmitButtonFullWidth
          type="submit"
          className={cx('btn btn-primary', submitBtnClassName)}
          disabled={!canSubmit}
        >
          Subscribe
        </SubmitButtonFullWidth>
      </form>
    </fieldset>
  );

  return (
    <>
      {status === Status.NOT_SENT && !email && renderGuestForm()}
      {status === Status.NOT_SENT && email && renderMemberForm()}
      <MessageSection>
        {status === Status.SENT && !submitFailed && (
          <span>Please check your inbox. A newsletter confirmation email has been sent to your email address</span>
        )}
        {status === Status.OPTED_IN && <span>Your email is already on our list</span>}
        {submitFailed && <span>An error occurred please try again.</span>}
      </MessageSection>
    </>
  );
};

const Input = styled.input`
  width: 215px;
  font-size: 1.15rem;
  height: 49px;
  line-height: 49px;
  padding: 0 8px;
  border-radius: 0;
  display: inline-block;
  vertical-align: middle;

  &.has-error {
    border-color: red;
  }
`;

const SubmitButtonBase = styled.button`
  outline: none;
  border: 0;
  border-radius: 0;
  font-size: 1rem;
  height: 49px;
  line-height: 49px;
  padding: 0 15px;
  display: inline-block;
  vertical-align: middle;
`;

const SubmitButtonFullWidth = styled(SubmitButtonBase)`
  display: block;
  width: 100%;
  border-radius: 4px;
`;

const MessageSection = styled.div``;
